import { ButtonHTMLAttributes, ReactNode, forwardRef } from 'react'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'outline' | 'ghost' | 'link'
  size?: 'sm' | 'md' | 'lg' | 'icon'
  loading?: boolean
  icon?: ReactNode
  children?: ReactNode
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', loading = false, icon, children, disabled, ...props }, ref) => {
    const baseStyles = 'inline-flex items-center justify-center font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 rounded-lg whitespace-nowrap text-sm'

    const variants = {
      primary: 'bg-primary text-primary-foreground shadow hover:bg-primary/90',
      secondary: 'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
      success: 'bg-success-600 text-white shadow hover:bg-success-600/90',
      danger: 'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
      warning: 'bg-warning-500 text-white shadow hover:bg-warning-500/90',
      outline: 'border border-input bg-transparent shadow-sm hover:bg-accent hover:text-accent-foreground',
      ghost: 'hover:bg-accent hover:text-accent-foreground',
      link: 'text-primary underline-offset-4 hover:underline',
    }

    const sizes = {
      sm: 'h-9 md:h-8 px-3 text-xs md:text-xs',
      md: 'h-11 md:h-9 px-4 py-2 text-sm md:text-sm',
      lg: 'h-12 md:h-10 px-6 md:px-8 text-base md:text-base',
      icon: 'h-11 w-11 md:h-9 md:w-9',
    }

    return (
      <button
        className={cn(baseStyles, variants[variant], sizes[size], className)}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        {!loading && icon && <span className={cn("mr-2", size === 'icon' && "mr-0")}>{icon}</span>}
        {children}
      </button>
    )
  }
)

Button.displayName = "Button"

export default Button